import mqtt from "mqtt";
import type { MqttClient } from "mqtt";
import {
  applyMQTTConfig,
  buildMQTTBrokerUrl,
  isMQTTEnabled,
  mqttConfig,
} from "./mqttConfig";
import type { MQTTConfig } from "./mqttConfig";

const LOG = "[StormcloudVideoPlayer][MQTT]";

const KEEPALIVE_SECONDS = 30;
const CONNECT_TIMEOUT_MS = 10_000;

const RECONNECT_BASE_MS = 5_000;
const RECONNECT_MAX_MS = 60_000;
const MAX_RECONNECT_ATTEMPTS = 8;
const COOLDOWN_MS = 10 * 60_000;

export type MQTTStatus = "disconnected" | "connecting" | "connected" | "error";

let client: MqttClient | null = null;
let status: MQTTStatus = "disconnected";
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let stopped = false;

export function getMQTTStatus(): MQTTStatus {
  return status;
}

export function isMQTTConnected(): boolean {
  return status === "connected" && client !== null && client.connected;
}

export function isMQTTConfigured(): boolean {
  return isMQTTEnabled();
}

export function configureMQTT(overrides: Partial<MQTTConfig>): void {
  applyMQTTConfig(overrides);
  disconnectMQTT();
}

function clearReconnectTimer(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function teardownClient(): void {
  if (client) {
    try {
      client.removeAllListeners();
      client.end(true);
    } catch {
      // ignore teardown errors
    }
    client = null;
  }
}

function scheduleReconnect(): void {
  if (stopped || !isMQTTEnabled() || reconnectTimer) return;

  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.warn(
      `${LOG} giving up after ${reconnectAttempts} attempts; cooling down for ${
        COOLDOWN_MS / 60_000
      }m`
    );
    teardownClient();
    status = "disconnected";
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      reconnectAttempts = 0;
      openConnection();
    }, COOLDOWN_MS);
    return;
  }

  const delay = Math.min(
    RECONNECT_MAX_MS,
    RECONNECT_BASE_MS * 2 ** reconnectAttempts
  );
  const jitter = Math.floor(Math.random() * 1_000);
  reconnectAttempts += 1;

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    openConnection();
  }, delay + jitter);
}

function openConnection(): void {
  if (stopped || !isMQTTEnabled()) return;

  teardownClient();

  const url = buildMQTTBrokerUrl();
  status = "connecting";

  const clientId = `stormcloud-vp-${Math.random().toString(36).slice(2, 9)}`;

  try {
    client = mqtt.connect(url, {
      clientId,
      username: mqttConfig.username,
      password: mqttConfig.password,
      keepalive: KEEPALIVE_SECONDS,
      clean: true,
      // Disable MQTT.js internal reconnect; we manage reconnection ourselves.
      reconnectPeriod: 0,
      connectTimeout: CONNECT_TIMEOUT_MS,
      queueQoSZero: false,
      reschedulePings: true,
      // Root-cause fix for the Tizen 4.0 "Keepalive timeout" loop: MQTT.js's
      // default timerVariant "auto" runs the keepalive interval inside a
      // Blob-URL Web Worker (via worker-timers). On Samsung Tizen 4.0 (old
      // WebKit) served from a file:// origin that worker can't tick reliably,
      // so the keepalive counter trips onKeepaliveTimeout almost immediately
      // after connect. Force the native setInterval timer instead; the main
      // thread is free because video is decoded by the native AVPlay pipeline.
      timerVariant: "native",
    });
  } catch (err) {
    status = "error";
    console.warn(`${LOG} connect() threw:`, err);
    scheduleReconnect();
    return;
  }

  client.on("connect", () => {
    status = "connected";
    reconnectAttempts = 0;
    clearReconnectTimer();
    console.info(`${LOG} connected to ${url}`);
  });

  client.on("offline", () => {
    if (status === "connected") status = "disconnected";
    console.warn(`${LOG} offline`);
    scheduleReconnect();
  });

  client.on("error", (err) => {
    status = "error";
    console.warn(`${LOG} error:`, err.message);
    scheduleReconnect();
  });

  client.on("close", () => {
    if (status === "connected") status = "disconnected";
    scheduleReconnect();
  });
}

export function initMQTTClient(): void {
  if (client || !isMQTTEnabled()) return;
  stopped = false;
  reconnectAttempts = 0;
  openConnection();
}

export function ensureMQTTClient(): void {
  if (isMQTTEnabled() && !client && !reconnectTimer) {
    initMQTTClient();
  }
}

export function publishMQTT(
  topic: string,
  payload: Record<string, unknown>
): boolean {
  if (!isMQTTEnabled()) {
    return false;
  }

  ensureMQTTClient();

  if (!client || !client.connected) {
    return false;
  }

  try {
    client.publish(topic, JSON.stringify(payload), { qos: mqttConfig.qos });
    return true;
  } catch (err) {
    console.warn(`${LOG} publish failed on ${topic}:`, err);
    return false;
  }
}

export function disconnectMQTT(): void {
  stopped = true;
  clearReconnectTimer();
  teardownClient();
  reconnectAttempts = 0;
  status = "disconnected";
}
